import React from "react";
import {
  FaBuilding,
  FaCalendarAlt,
  FaEnvelope,
  FaKey,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegEye,
  FaRoute,
  FaShareAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  DEFAULT_GRADE_IMAGE,
  GRADE_IMAGES,
} from "../data/kataOptions";

import {
  formatDate,
  getDepartmentLabel,
  getInitials,
  getRoleLabel,
  sanitizePhoneForLink,
  truncateText,
} from "../utils/kataUtils";

export default function PartenaireKataCard({
  partenaire,
  onDetails,
  onManage,
}) {
  const gradeImage =
    GRADE_IMAGES[partenaire.passage_grade] ||
    DEFAULT_GRADE_IMAGE;

  const location = `${getDepartmentLabel(
    partenaire.departement
  )} · ${partenaire.region}`;

  const handleShare = async () => {
    const sharedUrl = new URL(
      window.location.href
    );

    sharedUrl.searchParams.set(
      "annonce",
      partenaire.id
    );

    const shareUrl = sharedUrl.toString();

    const shareData = {
      title: `Recherche partenaire kata - ${partenaire.prenom} ${partenaire.nom}`,
      text: `${partenaire.prenom} recherche un partenaire pour travailler ${
        partenaire.kata || "un kata"
      }.`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(
          shareUrl
        );

        toast.success(
          "Le lien de l’annonce a été copié."
        );
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        toast.error(
          "Le partage n’a pas pu être effectué."
        );
      }
    }
  };

  const handleDetails = () => {
    if (typeof onDetails === "function") {
      onDetails(partenaire);
    }
  };

  const handleManage = () => {
    if (typeof onManage === "function") {
      onManage(partenaire);
    } else {
      toast.error(
        "La gestion de l’annonce est momentanément indisponible."
      );
    }
  };

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[1.6rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-xl hover:shadow-slate-200/60 dark:border-white/10 dark:bg-slate-900 dark:hover:border-orange-400/30 dark:hover:shadow-black/20">
      <header className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 p-4 text-white">
        <div className="absolute -right-10 -top-12 h-32 w-32 rounded-full bg-orange-500/15 blur-2xl" />

        <div className="relative flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-sm font-semibold backdrop-blur">
              {getInitials(
                partenaire.prenom,
                partenaire.nom
              )}
            </span>

            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-300">
                Partenaire recherché
              </p>

              <h3 className="mt-1 truncate text-base font-semibold">
                {partenaire.prenom}{" "}
                {partenaire.nom}
              </h3>
            </div>
          </div>

          <button
            type="button"
            onClick={handleShare}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-sm text-white transition hover:bg-white/20"
            aria-label="Partager l’annonce"
            title="Partager l’annonce"
          >
            <FaShareAlt />
          </button>
        </div>

        <div className="relative mt-3 flex min-h-[66px] items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.07] px-3 py-2.5 backdrop-blur">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
              Passage préparé
            </p>

            <p className="mt-1 truncate text-lg font-semibold text-white">
              {partenaire.passage_grade}
            </p>
          </div>

          <div className="flex h-16 w-40 shrink-0 items-center justify-center rounded-xl border border-amber-200/60 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 px-2 shadow-inner">
            <img
              src={gradeImage}
              alt={`Ceinture ${partenaire.passage_grade}`}
              className="h-16 w-full object-contain drop-shadow-[0_3px_6px_rgba(0,0,0,0.3)]"
              loading="lazy"
            />
          </div>
        </div>
      </header>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-wrap gap-1.5">
          {partenaire.kata ? (
            <span className="max-w-full truncate rounded-full bg-orange-100 px-2.5 py-1 text-[11px] font-semibold text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
              {partenaire.kata}
            </span>
          ) : null}

          <span className="max-w-full truncate rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
            {getRoleLabel(
              partenaire.role_recherche
            )}
          </span>
        </div>

        <div className="mt-3 space-y-1.5">
          <CompactInfoLine
            icon={FaBuilding}
            value={partenaire.club}
          />

          <CompactInfoLine
            icon={FaMapMarkerAlt}
            value={location}
          />

          {partenaire.disponibilites ? (
            <CompactInfoLine
              icon={FaCalendarAlt}
              value={partenaire.disponibilites}
            />
          ) : null}

          {partenaire.mobilite ? (
            <CompactInfoLine
              icon={FaRoute}
              value={partenaire.mobilite}
            />
          ) : null}
        </div>

        {partenaire.content ? (
          <p className="mt-3 line-clamp-2 min-h-[40px] whitespace-pre-line text-sm leading-5 text-slate-600 dark:text-slate-300">
            {truncateText(
              partenaire.content,
              150
            )}
          </p>
        ) : null}

        <div className="mt-auto pt-3">
          <div className="flex items-center justify-between border-t border-slate-200 pt-3 dark:border-white/10">
            <p className="truncate pr-2 text-[11px] text-slate-400">
              {formatDate(
                partenaire.created_at
              )}
            </p>

            <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
          </div>

          <div className="mt-3 flex items-center gap-2">
            {partenaire.tel ? (
              <a
                href={`tel:${sanitizePhoneForLink(
                  partenaire.tel
                )}`}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 transition hover:-translate-y-0.5 hover:bg-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300"
                aria-label="Appeler"
                title="Appeler"
              >
                <FaPhoneAlt />
              </a>
            ) : null}

            {partenaire.mail ? (
              <a
                href={`mailto:${partenaire.mail}`}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700 transition hover:-translate-y-0.5 hover:bg-blue-200 dark:bg-blue-500/10 dark:text-blue-300"
                aria-label="Envoyer un email"
                title="Envoyer un email"
              >
                <FaEnvelope />
              </a>
            ) : null}

            <button
              type="button"
              onClick={handleDetails}
              className="flex h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl bg-slate-950 px-3 text-xs font-semibold text-white transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950"
            >
              <FaRegEye />
              Voir
            </button>

            {/* <button
              type="button"
              onClick={handleManage}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-orange-200 bg-orange-50 text-orange-600 transition hover:-translate-y-0.5 hover:border-orange-300 hover:bg-orange-100 dark:border-orange-400/20 dark:bg-orange-400/10 dark:text-orange-300"
              aria-label="Gérer mon annonce"
              title="Gérer mon annonce"
            >
              <FaKey />
            </button> */}
          </div>
        </div>
      </div>
    </article>
  );
}

function CompactInfoLine({ icon: Icon, value }) {
  if (!value) {
    return null;
  }

  return (
    <div
      className="flex min-w-0 items-center gap-2 rounded-xl px-1 py-1"
      title={value}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[11px] text-slate-500 dark:bg-white/5 dark:text-slate-400">
        <Icon />
      </span>

      <span className="min-w-0 truncate text-xs font-semibold text-slate-700 dark:text-slate-200">
        {value}
      </span>
    </div>
  );
}